import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useSearch } from "@redotech/react-util/search";
import ChevronsDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { TeamDataContext } from "@redotech/redo-web/arbiter-components/navigation/common/redo-nav-team-item";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { PlatformEvents } from "../../../platform-events";
import * as teamSwitcherCss from "./team-switcher.module.css";

interface TeamSwitcherProps {
  className?: string;
}

export const TeamSwitcher = memo(function TeamSwitcher({
  className,
}: TeamSwitcherProps) {
  const teamData = useRequiredContext(TeamDataContext);
  const { currentTeam, connectedStores, setCurrentTeam, addStore } = teamData;

  const connectedStoresHaveTickets = connectedStores.some(
    (store) => store.ticketCount > 0,
  );

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const searchableTeamItems: RedoListItem<string>[] = useMemo(() => {
    const currentTeamItem: RedoListItem<string> = {
      id: currentTeam.teamId,
      value: currentTeam.teamId,
      type: "text",
      text: currentTeam.name,
      leadingItem: {
        type: "avatar",
        imageUrl: currentTeam.logoUrl,
        alt: currentTeam.name,
        name: currentTeam.name,
      },
    };

    const connectedTeams: RedoListItem<string>[] = connectedStores.map(
      (connectedStore) => {
        return {
          id: connectedStore.teamId,
          value: connectedStore.teamId,
          type: "text",
          text: connectedStore.name,
          infoBadge:
            connectedStore.ticketCount > 0
              ? {
                  type: "alternate",
                  text: connectedStore.ticketCount,
                  color: "error" as const,
                }
              : undefined,
          leadingItem: {
            type: "avatar",
            imageUrl: connectedStore.logoUrl,
            alt: connectedStore.name,
            name: connectedStore.name,
          },
        };
      },
    );

    return [currentTeamItem, ...connectedTeams];
  }, [currentTeam, connectedStores]);

  const fuseSearcher = useMemo(() => {
    return new Fuse(searchableTeamItems, { keys: ["text"], threshold: 0.2 });
  }, [searchableTeamItems]);

  const filteredTeams = useSearch(
    fuseSearcher,
    searchableTeamItems,
    searchText,
  );

  const teamSwitcherList: RedoListItem<string>[] = useMemo(() => {
    const addTeam: RedoListItem<string> = {
      id: "add-team",
      type: "text",
      text: "Add another store",
      leadingItem: { type: "icon", Icon: PlusIcon },
      value: "add-team",
    };

    return [...filteredTeams, addTeam];
  }, [filteredTeams]);

  return (
    <>
      <button
        className={`${teamSwitcherCss.teamSwitcherButton} ${className || ""}`}
        onClick={() => {
          if (!dropdownOpen) {
            amplitude.track(PlatformEvents.NAV_TEAM_SWITCHER_OPENED);
          }
          setDropdownOpen((old) => !old);
        }}
        ref={setDropdownAnchor}
      >
        <RedoAvatar
          content={{
            type: "image",
            imageUrl: currentTeam.logoUrl,
            altText: currentTeam.name,
            backup: { type: "text", text: currentTeam.name },
          }}
          size="xs"
          status={
            connectedStoresHaveTickets
              ? { type: "presence", color: "red" }
              : undefined
          }
        />
        <span className={teamSwitcherCss.teamName}>{currentTeam.name}</span>
        <ChevronsDownIcon className={teamSwitcherCss.chevronIcon} />
      </button>
      <RedoListDropdown
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={dropdownOpen}
        items={teamSwitcherList}
        itemSelected={({ item }) => {
          if (item.id === "add-team") {
            addStore();
          } else {
            amplitude.track(PlatformEvents.NAV_TEAM_SWITCHED, {
              teamId: item.value,
            });
            setCurrentTeam(item.value);
          }
        }}
        searchPlaceholder="Search stores..."
        searchString={searchText}
        selectedItems={currentTeam.teamId}
        setDropdownOpen={(open) => {
          setDropdownOpen(open);
          if (!open) {
            setSearchText("");
          }
        }}
        setSearchString={setSearchText}
        size="sm"
      />
    </>
  );
});
